// Phase 8.1 — Lightweight portfolio number / currency formatters.
// Uses the user's browser locale where reasonable so EU users see
// 1.234,56 € and US users see €1,234.56. Currency code defaults to EUR
// since pricing is EUR; positions hold per-ticker currency that the
// holdings table renders verbatim.

export function fmtCurrency(value, opts = {}) {
  // Phase 8.1.c — per-position currency. Callers MUST pass the
  // resolved currency for that position; we ONLY fall back to EUR
  // when the caller explicitly opts in via `{ currency: 'EUR' }`.
  // We never silently coerce USD prices to EUR (which previously
  // mis-displayed US fair values in € for German users).
  const { signed = false, ifNull = null, currency = "USD", maxFractionDigits = 2 } = opts;
  if (value === null || value === undefined || Number.isNaN(value)) return ifNull;
  try {
    const v = Number(value);
    const fmt = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: maxFractionDigits,
      minimumFractionDigits: Math.abs(v) < 10 ? Math.min(2, maxFractionDigits) : 0,
    });
    const out = fmt.format(v);
    if (signed && v > 0) return `+${out}`;
    return out;
  } catch {
    return String(value);
  }
}

export function fmtPercent(value, opts = {}) {
  const { ifNull = "—", digits = 2 } = opts;
  if (value === null || value === undefined || Number.isNaN(value)) return ifNull;
  const v = Number(value);
  // Inputs are decimals (0.12 => 12.0%). If a caller passes 12 expecting
  // 12%, they should divide first. We expose two helpers to keep that
  // contract obvious.
  return `${(v * 100).toFixed(digits)}%`;
}

export function fmtScore(value, opts = {}) {
  const { ifNull = "—", digits = 1 } = opts;
  if (value === null || value === undefined || Number.isNaN(value)) return ifNull;
  return Number(value).toFixed(digits);
}

export function fmtNumber(value, opts = {}) {
  const { ifNull = "—", digits = 2 } = opts;
  if (value === null || value === undefined || Number.isNaN(value)) return ifNull;
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

// Phase 8.PH — Locale-aware compact currency formatter used by the
// responsive KPI cards. Only invoked when the exact currency string
// cannot fit safely inside the card. Returns e.g. "$1.24M", "€3,4 Mio".
// We ALWAYS keep the exact value on `title` / `aria-label` at the call
// site so accessibility + copy-paste still expose the full number.
export function fmtCurrencyCompact(value, opts = {}) {
  const { signed = false, ifNull = null, currency = "USD" } = opts;
  if (value === null || value === undefined || Number.isNaN(value)) return ifNull;
  try {
    const v = Number(value);
    const fmt = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: Math.abs(v) < 10 ? 2 : 1,
      minimumFractionDigits: 0,
    });
    const out = fmt.format(v);
    if (signed && v > 0) return `+${out}`;
    return out;
  } catch {
    return String(value);
  }
}
